import React, { useEffect, useState } from 'react';
import { BOAPI } from '../service/UsersService';

const todayStr = () => {
  const d = new Date();
  return `${d.getFullYear()}-${String(d.getMonth() + 1).padStart(2, '0')}-${String(d.getDate()).padStart(2, '0')}`;
};

const QUICK_OPTIONS = [
  { label: 'בעוד שבוע', days: 7 },
  { label: 'בעוד חודש', days: 30 },
  { label: 'בעוד 3 חודשים', days: 90 },
];

const addDays = (days) => {
  const d = new Date();
  d.setDate(d.getDate() + days);
  return `${d.getFullYear()}-${String(d.getMonth() + 1).padStart(2, '0')}-${String(d.getDate()).padStart(2, '0')}`;
};

// מעקב ידני לעסק שממתין לאישור בעלים - "סירב", "לחזור אליו בעוד חודש" וכו'.
// כפתורי קיצור למועדים נפוצים + שדה תאריך חופשי + הערה. "ניקוי מעקב" מוחק
// את שניהם (למשל אחרי שחזרו אליו בפועל וסיכמו איתו). business=null סוגר
const FollowUpModal = ({ business, onClose, onSaved }) => {
  const [followUpAt, setFollowUpAt] = useState('');
  const [note, setNote] = useState('');
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState('');

  useEffect(() => {
    if (!business) return;
    setFollowUpAt(business.followUpAt ? new Date(business.followUpAt).toISOString().slice(0, 10) : '');
    setNote(business.followUpNote || '');
    setError('');
  }, [business]);

  if (!business) return null;

  const save = async (clearedAt, clearedNote) => {
    setSaving(true);
    setError('');
    try {
      const resp = await BOAPI.setBusinessFollowUp(business.id, clearedAt ?? (followUpAt || null), clearedNote ?? (note.trim() || null));
      if (resp?.[0]?.[0]?.code === 0) {
        onSaved({ id: business.id, ...resp[1]?.[0] });
      } else {
        setError(resp?.[0]?.[0]?.msg || 'השמירה נכשלה');
      }
    } catch {
      setError('שגיאה בתקשורת עם השרת');
    } finally {
      setSaving(false);
    }
  };

  return (
    <div style={s.overlay} onClick={() => !saving && onClose()}>
      <div style={s.card} onClick={(e) => e.stopPropagation()}>
        <h3 style={s.title}>מעקב - {business.name}</h3>
        <p style={s.hint}>סמנו מתי לחזור לבית העסק הזה, עם הערה חופשית (למשל "סירב, לנסות שוב אחרי החגים").</p>

        {error && <div style={s.errorBanner}>❌ {error}</div>}

        <div style={s.quickRow}>
          {QUICK_OPTIONS.map((opt) => (
            <button key={opt.label} type="button" style={s.quickBtn} onClick={() => setFollowUpAt(addDays(opt.days))}>
              {opt.label}
            </button>
          ))}
        </div>

        <label style={s.label}>תאריך לחזרה</label>
        <input type="date" value={followUpAt} min={todayStr()} onChange={(e) => setFollowUpAt(e.target.value)} style={s.input} />

        <label style={s.label}>הערה</label>
        <textarea
          value={note}
          onChange={(e) => setNote(e.target.value)}
          maxLength={300}
          rows={3}
          placeholder='לדוגמה: "סירב, מתעניין רק אחרי החגים"'
          style={s.textarea}
        />

        <div style={s.actions}>
          {(business.followUpAt || business.followUpNote) && (
            <button type="button" style={s.clearBtn} disabled={saving} onClick={() => save(null, null)}>
              ניקוי מעקב
            </button>
          )}
          <button type="button" style={s.cancelBtn} disabled={saving} onClick={onClose}>ביטול</button>
          <button type="button" style={s.saveBtn} disabled={saving} onClick={() => save()}>
            {saving ? 'שומר...' : 'שמירה'}
          </button>
        </div>
      </div>
    </div>
  );
};

const s = {
  overlay: {
    position: 'fixed', inset: 0, background: 'rgba(15,23,42,0.6)',
    display: 'flex', alignItems: 'center', justifyContent: 'center',
    zIndex: 300, padding: 16, direction: 'rtl',
  },
  card: {
    width: '100%', maxWidth: 420,
    background: '#fff', borderRadius: 20, boxShadow: '0 20px 60px rgba(0,0,0,0.35)',
    padding: '24px 22px',
  },
  title: { margin: '0 0 6px', color: '#1a1f36', fontSize: 17, fontWeight: 800 },
  hint: { margin: '0 0 16px', color: '#8792a2', fontSize: 12.5, lineHeight: 1.6 },
  errorBanner: { background: '#ffebee', color: '#c62828', padding: 10, borderRadius: 8, marginBottom: 14, fontSize: 13 },
  quickRow: { display: 'flex', gap: 8, marginBottom: 16, flexWrap: 'wrap' },
  quickBtn: { padding: '7px 12px', background: '#EEF2FF', color: '#1f6feb', border: '1px solid #c7d7fb', borderRadius: 999, cursor: 'pointer', fontSize: 12, fontWeight: 700 },
  label: { display: 'block', fontSize: 12.5, fontWeight: 700, color: '#1a1f36', marginBottom: 6 },
  input: { width: '100%', padding: '9px 12px', borderRadius: 10, border: '1.5px solid #e3e8ee', fontSize: 14, boxSizing: 'border-box', marginBottom: 14, fontFamily: 'inherit' },
  textarea: { width: '100%', padding: '9px 12px', borderRadius: 10, border: '1.5px solid #e3e8ee', fontSize: 13.5, boxSizing: 'border-box', marginBottom: 6, resize: 'vertical', fontFamily: 'inherit', direction: 'rtl', textAlign: 'right' },
  actions: { display: 'flex', gap: 8, marginTop: 16 },
  clearBtn: { padding: '10px 14px', background: '#fff', color: '#c62828', border: '1.5px solid #fbc4c4', borderRadius: 10, cursor: 'pointer', fontSize: 13, fontWeight: 700 },
  cancelBtn: { flex: 1, padding: '10px 14px', background: '#f5f7fa', color: '#4f566b', border: '1.5px solid #e3e8ee', borderRadius: 10, cursor: 'pointer', fontSize: 13.5, fontWeight: 700 },
  saveBtn: { flex: 1, padding: '10px 14px', background: '#1f6feb', color: '#fff', border: 'none', borderRadius: 10, cursor: 'pointer', fontSize: 13.5, fontWeight: 700 },
};

export default FollowUpModal;
